/*
 * @Copyright 2023-2024. Institute for Future Intelligence, Inc.
 */

import { createWithEqualityFn } from 'zustand/traditional';
import { RefObject } from 'react';
import { Group, Mesh, PerspectiveCamera, Raycaster, Vector2, Vector3, WebGLRenderer } from 'three';
import { Molecule } from '../models/Molecule.ts';
import { Camera, invalidate } from '@react-three/fiber';
import { VdwBond } from '../models/VdwBond.ts';
import { MolecularDynamics } from '../models/MolecularDynamics.ts';
import { CrystalDynamics } from '../models/crystal/CrystalDynamics.ts';
import { MAXIMUM_NUMBER_OF_ATOMS_FOR_SIMULATION } from '../constants.ts';
import i18n from '../i18n/i18n';
import { useStore } from './common.ts';
import { MyTrackballControls } from '../js/MyTrackballControls';
import { setMessage } from '../helpers.tsx';
import type { BioPartialMDSnapshot } from '../types.ts';

export interface RefStoreState {
  selectNone: () => void;
  dashboardDivRef: RefObject<HTMLDivElement> | null;
  controlsRef: RefObject<MyTrackballControls> | null;
  cameraRef: RefObject<Camera | undefined> | null;
  raycasterRef: RefObject<Raycaster | undefined> | null;
  planeXYRef: RefObject<Mesh> | null;
  planeYZRef: RefObject<Mesh> | null;
  planeXZRef: RefObject<Mesh> | null;
  ligandRef: RefObject<Group> | null;
  // The docking search-region sphere group. Published so the rigid-body relax
  // (the Dock button) can keep it centered on the ligand as the ligand moves —
  // the sphere is otherwise positioned from ligandTransform, which the relax
  // commits only once, at the end of the run.
  searchRegionRef: RefObject<Group> | null;
  moleculesRef: RefObject<Molecule[]> | null;
  vdwBondsRef: RefObject<VdwBond[]> | null;
  clickPointRef: RefObject<Vector3> | null;
  molecularDynamicsRef: RefObject<MolecularDynamics> | null;
  // Crystal-side MD ref — independent of molecularDynamicsRef so the two
  // engines don't interfere. Populated by CrystalViewer when chamberCrystal
  // / chamberSupercell change; consumed by CrystalSimulationControls.
  crystalDynamicsRef: RefObject<CrystalDynamics> | null;
  // Where the crystal lab's Paste should drop a copy, for the last right-click:
  // the emptiest point (engine frame) on the line through the clicked pixel,
  // and its clearance (Å). Computed on demand. null before any right-click.
  crystalPastePoint: (() => { point: Vector3; clearance: number } | null) | null;
  // Partial (region-restrained) biomolecular MD — published by bioPartialMD.tsx
  // so the save path can pull the live trajectory (coords + velocities + step)
  // on demand without that heavy state living in a reactive store. null when no
  // bio MD runner is mounted.
  bioPartialMDRef: RefObject<{ getSnapshot: () => BioPartialMDSnapshot | null }> | null;
  // The chem-lib Complex the Protein / DNA-RNA Lab viewer currently renders,
  // tagged by structure name. Imperative (never drives a re-render): the agent's
  // showSecondaryStructure reads each residue's secondary-structure element off
  // it so the chat track matches the cartoon exactly. null when nothing is shown.
  bioComplex: { name: string; complex: unknown } | null;
  // Snapshot of the Protein / DNA-RNA Lab canvas once the scene has settled
  // (no parse or build pending, nothing changed for a moment) — the lab report
  // replays tour stops and calls this at each one. JPEG data URL, or null when
  // the lab is not mounted / the canvas cannot be read. Published by
  // bioStructureViewer; null when no bio lab is mounted.
  bioSceneSnapshot: ((opts?: { width?: number; shouldAbort?: () => boolean }) => Promise<string | null>) | null;
  // Ask the bio lab canvas (frameloop="demand") for a fresh frame after an
  // imperative camera change. null when no bio lab is mounted.
  bioSceneInvalidate: (() => void) | null;
  chamberViewerCanvas: { gl: WebGLRenderer; camera: PerspectiveCamera } | null;
  galleryViewerCanvas: { gl: WebGLRenderer } | null;
  resizeCanvases: (percentWidth: number) => void;
  warnIfTooManyAtoms: (addition: number) => void;
}

export const useRefStore = createWithEqualityFn<RefStoreState>()((set, get) => {
  return {
    selectNone: () => {},
    controlsRef: null,
    cameraRef: null,
    raycasterRef: null,
    planeXYRef: null,
    planeYZRef: null,
    planeXZRef: null,
    ligandRef: null,
    searchRegionRef: null,
    moleculesRef: null,
    vdwBondsRef: null,
    clickPointRef: null,
    molecularDynamicsRef: null,
    crystalDynamicsRef: null,
    crystalPastePoint: null,
    bioPartialMDRef: null,
    bioComplex: null,
    bioSceneSnapshot: null,
    bioSceneInvalidate: null,
    chamberViewerCanvas: null,
    galleryViewerCanvas: null,
    dashboardDivRef: null,

    resizeCanvases(percentWidth) {
      const chamberViewerCanvas = get().chamberViewerCanvas;
      const galleryViewerCanvas = get().galleryViewerCanvas;
      const v = new Vector2();

      if (chamberViewerCanvas) {
        const { gl, camera } = chamberViewerCanvas;
        const newWidth = ((100 - percentWidth) * window.innerWidth) / 100;
        gl.getSize(v);
        gl.setSize(newWidth, v.y);
        camera.aspect = newWidth / v.y;
        camera.updateProjectionMatrix();
        invalidate();
      }
      if (galleryViewerCanvas) {
        const { gl } = galleryViewerCanvas;
        const newWidth = (percentWidth * window.innerWidth) / 100;
        gl.getSize(v);
        gl.setSize(newWidth, v.y);
      }
    },

    warnIfTooManyAtoms(addition: number) {
      const mdRef = get().molecularDynamicsRef;
      const atomCount = (mdRef?.current?.atoms.length ?? 0) + addition;
      if (atomCount > MAXIMUM_NUMBER_OF_ATOMS_FOR_SIMULATION) {
        const lang = { lng: useStore.getState().language };
        setMessage(
          'warning',
          i18n.t('message.TooManyAtomsMayCauseSimulationToRunSlowly', lang) +
            ' (' +
            atomCount +
            ' ' +
            i18n.t('word.AtomsLowerCasePlural', lang) +
            ').',
        );
      }
    },
  };
});
